import { ArticleCard } from "../components/sections/Cards";
import CtaPanel from "../components/sections/CtaPanel";
import PageHero from "../components/sections/PageHero";
import StatsBand, { wastedLine } from "../components/sections/StatsBand";
import TextLink from "../components/ui/TextLink";
import { insights, pages } from "../content";
import { routes } from "../data/site";
import { pathnameOf } from "../lib/links";

const archivePath = /^\/(tag|author|category)\/([^/]+)\//;
const archiveLabels = { tag: "Tag", author: "Author", category: "Category" };

const blog = {
  eyebrow: "Blog",
  title: "Stay ahead in *construction.*",
  lede: "Insights and company news: practical thinking on software, process and technology from people who build for the job site.",
};

const blogPaths = new Set(insights.map((card) => pathnameOf(card.href)));

/**
 * Which archive is showing. Routes pass `path`; failing that, the archive
 * page is the captured page whose cards were passed in.
 */
function archiveFor(path, items) {
  const page = path
    ? pages.find((p) => p.path === path)
    : pages.find((p) => archivePath.test(p.path) && p.cards === items);
  const match = page?.path.match(archivePath);
  return match && { type: match[1], slug: match[2] };
}

const titleCase = (slug) =>
  slug
    .split("-")
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ");

function Stats() {
  return (
    <section className="blueprint px-4 py-20 text-white sm:px-6 md:py-24">
      <div className="mx-auto max-w-280">
        <p className="font-display mx-auto mb-14 max-w-205 text-center text-[28px] leading-[1.15] uppercase md:text-[38px]">
          {wastedLine}
        </p>
        <StatsBand />
      </div>
    </section>
  );
}

/**
 * The blog. Tag, author and category archives pass `title`, plus `path` when
 * the route provides it. (`kind` is accepted for older callers; always blog.)
 */
export default function ListingPage({ items, title, path }) {
  const archive = title ? archiveFor(path, items) : undefined;

  // Archive pages captured a mix of team, case-study and blog cards; list
  // only the blog posts, or every post when the archive had none.
  let posts = items;
  if (title) {
    posts = items.filter((card) => blogPaths.has(pathnameOf(card.href)));
    if (!posts.length) posts = insights;
  }

  const heading = archive
    ? archive.type === "author"
      ? titleCase(archive.slug)
      : title
    : title || blog.title;
  const lede = archive
    ? archive.type === "author"
      ? "Recent posts from the Xeratec blog."
      : `Posts from the Xeratec blog filed under “${title}”.`
    : blog.lede;
  const [featured, ...rest] = archive ? [undefined, ...posts] : posts;

  return (
    <>
      <PageHero
        eyebrow={
          archive ? `Blog · ${archiveLabels[archive.type]}` : blog.eyebrow
        }
        title={heading}
        lede={lede}
      >
        {archive && (
          <TextLink
            href={routes.insights}
            className="text-yellow hover:text-yellow-hi"
          >
            All blog posts
          </TextLink>
        )}
      </PageHero>

      <section className="bg-surface px-4 py-16 sm:px-6 md:py-20">
        <div className="mx-auto max-w-280">
          <>
            {featured && (
              <div className="grid grid-cols-1">
                <ArticleCard
                  card={featured}
                  featured
                  eyebrow="Latest post"
                  headingLevel="h2"
                />
              </div>
            )}
            {rest.length > 0 && (
              <>
                <h2
                  className={
                    featured ? "eyebrow mt-16 mb-6 text-blue-deep" : "sr-only"
                  }
                >
                  {featured ? "More from the blog" : "Posts"}
                </h2>
                <ul className="grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-3">
                  {rest.map((card) => (
                    <li key={card.href} className="grid">
                      <ArticleCard card={card} />
                    </li>
                  ))}
                </ul>
              </>
            )}
          </>
        </div>
      </section>

      <Stats />
      <CtaPanel className="bg-white" />
    </>
  );
}
